import { Indicator, Root } from "@radix-ui/react-checkbox";
import {
  RegisterOptions,
  Control,
  Controller,
  FieldError,
  ControllerRenderProps,
  ControllerFieldState,
} from "react-hook-form";
import { InputError, ariaControlledBorder } from "../InputError";
import classNames from "classnames";
import Icon from "~/assets/icons/Icon";
import { ReactNode, useEffect, useState } from "react";

type CheckboxProps = {
  id: string;
  label: ReactNode;
  children?: React.ReactNode;
  className?: string;
  defaultChecked?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  validation?: RegisterOptions;
  hideLabel?: boolean;
} & Partial<React.ComponentProps<typeof Root>>;

export const Checkbox = (props: CheckboxProps) => {
  const {
    id,
    className,
    hideLabel,
    label,
    validation,
    children,
    ...restProps
  } = props;

  const [validationError, setValidationError] = useState<
    FieldError | undefined
  >(undefined);

  const onValidationError = (error: FieldError | undefined) => {
    setValidationError(error);
  };

  return (
    <div className={className}>
      <div className={classNames("flex items-center relative")}>
        <CheckboxTickarea
          {...restProps}
          id={id}
          className={classNames("shrink-0")}
          onValidationError={onValidationError}
          hideError
          validation={validation}
        />

        <label
          htmlFor={id}
          className={classNames(
            "pl-2.5 cursor-pointer select-none",
            hideLabel && "sr-only"
          )}
        >
          {label}
          {validation?.required ? "*" : ""}
        </label>
        {children}
      </div>
      <InputError>{validationError?.message}</InputError>
    </div>
  );
};

type CheckboxTickareaProps = {
  id: string;
  className?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  validation?: RegisterOptions;
  onValidationError?: (error: FieldError | undefined) => void;
  hideError?: boolean;
} & Partial<React.ComponentProps<typeof Root>>;

export function CheckboxTickarea(props: CheckboxTickareaProps) {
  const { id, control, validation, hideError = false, ...restProps } = props;

  return (
    <Controller
      name={id}
      control={control}
      rules={validation}
      render={({ field, fieldState }) => (
        <div>
          <ControlledCheckboxTickarea
            id={id}
            field={field}
            fieldState={fieldState}
            {...restProps}
          />
          {!hideError && <InputError>{fieldState.error?.message}</InputError>}
        </div>
      )}
    />
  );
}

type ControlledCheckboxTickareaProps = {
  className?: string;
  field: ControllerRenderProps;
  fieldState: ControllerFieldState;
  onValidationError?: (error: FieldError | undefined) => void;
} & Partial<React.ComponentProps<typeof Root>>;

function ControlledCheckboxTickarea(props: ControlledCheckboxTickareaProps) {
  const { className, field, fieldState, onValidationError, ...restProps } =
    props;

  useEffect(() => {
    onValidationError?.(fieldState.error);
  }, [fieldState.error]);

  return (
    <UncontrolledCheckboxTickarea
      {...restProps}
      className={classNames(
        "bg-white data-[state=checked]:bg-primary-500 h-[26px] w-[26px] border border-secondary-200 data-[state=checked]:border-primary-500 rounded-md appearance-none flex items-center justify-center",
        ariaControlledBorder,
        className
      )}
      value={undefined}
      checked={field.value}
      onCheckedChange={field.onChange}
      autoFocus={Boolean(fieldState.error)}
      aria-invalid={Boolean(fieldState.error)}
    />
  );
}

const UncontrolledCheckboxTickarea = (
  props: Partial<React.ComponentProps<typeof Root>>
) => {
  const { className, ...restProps } = props;
  return (
    <Root
      {...restProps}
      className={classNames(
        "bg-white data-[state=checked]:bg-primary-500 h-[26px] w-[26px] border border-secondary-200 data-[state=checked]:border-primary-500 rounded-md appearance-none flex items-center justify-center",
        className
      )}
    >
      <Indicator className="text-white">
        <Icon name="check" />
      </Indicator>
    </Root>
  );
};
